import { useEffect, useState } from 'react'
import { BarChart3, Rocket, Users as UsersIcon, Eye } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getAppStats, getCreatorAppStats, type AppStats, type CreatorAppStats } from '../../lib/api'
import IncludeOwnerToggle from './IncludeOwnerToggle'

export default function AppUsageStats({ appName }: { appName: string }) {
  const [stats, setStats] = useState<AppStats | null>(null)
  const [creatorStats, setCreatorStats] = useState<CreatorAppStats | null>(null)
  const [includeOwner, setIncludeOwner] = useState(false)
  const [onlyPublished, setOnlyPublished] = useState(false)

  useEffect(() => {
    Promise.all([
      getAppStats(appName, '30d'),
      getCreatorAppStats(appName).catch(() => null),
    ])
      .then(([appStats, creator]) => {
        setStats(appStats)
        if (creator) setCreatorStats(creator)
      })
      .catch(() => {})
  }, [appName])

  if (!stats) return null

  // Pick all-slot or prod-only field based on the published toggle;
  // owner toggle then folds in the owner's own usage. The owner is a
  // single user_id disjoint from the external set, so summing is right.
  const baseExternal = creatorStats
    ? onlyPublished
      ? creatorStats.visit_count_prod_30d
      : creatorStats.visit_count_30d
    : 0
  const baseExternalUnique = creatorStats
    ? onlyPublished
      ? creatorStats.unique_visitors_prod_30d
      : creatorStats.unique_visitors_30d
    : 0
  const baseOwner = creatorStats
    ? onlyPublished
      ? creatorStats.owner_visit_count_prod_30d
      : creatorStats.owner_visit_count_30d
    : 0
  const baseOwnerUnique = creatorStats
    ? onlyPublished
      ? creatorStats.owner_unique_visitors_prod_30d
      : creatorStats.owner_unique_visitors_30d
    : 0
  const visits = baseExternal + (includeOwner ? baseOwner : 0)
  const uniqueVisitors = baseExternalUnique + (includeOwner ? baseOwnerUnique : 0)
  const ownerHasVisits = (creatorStats?.owner_visit_count_30d ?? 0) > 0

  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Usage Stats <span className="text-xs font-normal text-muted-foreground">(30d)</span>
        </h2>
        {creatorStats && (
          <div className="flex items-center gap-3 flex-wrap">
            <IncludeOwnerToggle
              checked={onlyPublished}
              onCheckedChange={setOnlyPublished}
              label="Only published"
            />
            {(ownerHasVisits || includeOwner) && (
              <IncludeOwnerToggle checked={includeOwner} onCheckedChange={setIncludeOwner} />
            )}
          </div>
        )}
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-muted-foreground text-sm">
            <Rocket className="w-4 h-4" />
            Builds
          </span>
          <span className="font-medium">{stats.build_count}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-muted-foreground text-sm">
            <Rocket className="w-4 h-4" />
            Deploys
          </span>
          <span className="font-medium">{stats.deploy_count}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-muted-foreground text-sm">
            <UsersIcon className="w-4 h-4" />
            Shared With
          </span>
          <span className="font-medium">{stats.shared_with_count} users</span>
        </div>
        {creatorStats && (
          <>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-muted-foreground text-sm">
                <Eye className="w-4 h-4" />
                Visitors
              </span>
              <span className="font-medium">{visits}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-muted-foreground text-sm">
                <Eye className="w-4 h-4" />
                Unique Visitors
              </span>
              <span className="font-medium">{uniqueVisitors}</span>
            </div>
          </>
        )}
      </div>
      <Link to="/creator-dashboard" className="text-xs text-primary hover:underline mt-3 block">
        View all analytics &rarr;
      </Link>
    </div>
  )
}
